import { useState, useEffect } from "react";
import React from "react";
import {
  meter,
  asset,
  location,
  meterMeasurementUnit,
} from "../../api";
import { toast } from "react-toastify";
import {
  getEmptyField,
  printLocation,
  renderImage,
} from "../../utils/helper";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useDashboardContext } from "../../contexts/NewDashboardContext";
import LoadingIndicator from "../../components/LoadingIndicator";
import NormalInput from "../../components/form/HorizontalNormalInput";
import NormalSelect from "../../components/form/HorizontalNormalSelect";
import NormalTextArea from "../../components/form/HorizontalNormalTextarea";
import { Card, CardBody, CardHeader } from "../../components/card/card";
import ImageViewer from "../../components/ImageViewer";
import ConfirmationDlg from "../../components/ConfirmationDlg";
import MultipleFilesAttachments from "../../components/MultipleFilesAttachments";
import { LocationFilter } from "../../components/LocationFilter";
import Select from "react-select";


const colourStyles = {
  control: (styles) => ({
    ...styles,
    backgroundColor: "rgba(0, 0, 0, 0.48)",
    borderColor: "var(--bs-border-color)",
  }),
  input: (styles) => ({
    ...styles,
    color: "white",
  }),
  menu: (styles) => ({
    ...styles,
    color: "grey",
  }),
  singleValue: (styles) => ({
    ...styles,
    color: "white",
  }),
};



function setupLocationValues(locationSeq, location) {
  locationSeq.push(location.id);
  if (location.parent_location) {
    setupLocationValues(locationSeq, location.parent_location);
  }
}

const defaultUnits = [
  // Length
  "Miles",
  "Feet",
  "Inches",
  "Kilometers",
  "Meters",
  "Centimeters",
  "Millimeters",

  // Volume
  "Gallons",
  "Liters",
  "Milliliters",
  "Cubic Meters",

  // Temperature
  "Celsius",
  "Fahrenheit",
  "Kelvin",

  // Other
  "Hours",
  "Cycles",
  "PSI",
  "Kilograms",
  "Grams",
  "Volts",
  "Volts", // appears twice in your list
  "dBm",
  "N·m",

  "RMS Velocity",
  "RMS Acceleration"
];


function AddMeter({ afterSubmitCallback }) {
  const { id } = useParams();
  const editMode = !!id;
  const { locations } = useDashboardContext();
  const [selectedFile, setSelectedFile] = useState("");
  const [assets, setAssets] = useState([]);

  const [attachments, setAttachments] = useState([]);
  const { getLocationName, config } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [sublocations, setSublocations] = useState([]);
  const [locationValue, setLocationValue] = useState([]);
  const [units, setUnits] = useState([]);
  const [newUnit, setNewUnit] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedLocationPrev, setSelectedLocationPrev] = useState([]);

  const [dataToSend, setDataToSend] = useState({
    meter_name: "",
    meter_type: "manual", // manual or automated
    description: "",
    unit: "",
    asset_id: "",
    location_id: "",
    reading_frequency_value: "",
    reading_frequency_unit: "none",
    status: "active",
    warning_threshold: "",
    alarm_threshold: "",
  });

  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const [emptyField, setEmptyField] = useState([]);

  function backToList() {
    navigate(-1);
  }

  async function getMasterData() {
    try {
      setIsLoading(true);


      const resUnit = await meterMeasurementUnit.list({})
      if (resUnit.ok) {
        const body = await resUnit.json();
        let data = body.data;
        if (data) {
          const filteredDefaultUnits = defaultUnits.filter(unit => !data.some(d => d.name.toLowerCase() === unit.toLowerCase())).map(unit => ({name:unit,id:unit}));
          const merge = [...data, ...filteredDefaultUnits];
          merge.sort((a, b) => a.name.localeCompare(b.name));
          setUnits(merge);
        }
      }



      setSublocations([locations]);

      if (editMode) {
        await getRecord();
      }
    } catch (err) {
    } finally {
      setIsLoading(false);
    }
  }

  async function getRecord() {
    const result = await meter.getOne(id);

    if (result.ok) {
      const body = await result.json();
      let data = body.data;

      const defaultValue = {
        meter_name: data.meter_name,
        meter_type: data.meter_type || "manual",
        description: data.description,
        unit_id: data.unit_id || "",
        asset_id: data.asset_id || "",
        location_id: data.location_id || "",
        reading_frequency_value: data.reading_frequency_value || "",
        reading_frequency_unit: data.reading_frequency_unit || "none",
        status: data.status || "active",
        warning_threshold: data.warning_threshold || "",
        alarm_threshold: data.alarm_threshold || "",
      };

      if (data.asset?.location) {
        //find parent
        const locationSeq = [];
        setupLocationValues(locationSeq, data.asset.location);
        locationSeq.reverse();
        setSelectedLocation(data.asset.location);
        setSelectedLocationPrev(locationSeq);
      }
      else if (data.location) {
        const locationSeq = [];
        setupLocationValues(locationSeq, data.location);
        locationSeq.reverse();
        setSelectedLocation(data.location);
        setSelectedLocationPrev(locationSeq);
      }

      const att = [];
      if (data.attachments)
        Object.keys(data.attachments).forEach((key) => {
          if (data.attachments[key]) {
            const name = key.split("_")[3];
            const type = key.split("_")[2];
            const url = renderImage(
              `machine/attachment/${data.attachments[key]}`
            );;
            const attachment = {
              origin_key: key,
              exist: true,
              name: name,
              type,
              url,
              value: data.attachments[key],
            };
            att.push(attachment);
          }
        });
      setAttachments(att);
      setDataToSend(defaultValue);
    }
  }

  useEffect(() => {
    if (locations.length) getMasterData();
  }, [locations]);


  useEffect(() => {
    let sublocations = locations;
    const copy = [sublocations];

    const findSublocation = (lv, index) => {
      const location = sublocations.find((l) => l.id == lv);

      if (
        location &&
        location.sublocations &&
        location.sublocations.length > 0
      ) {
        copy.splice(index + 1);
        copy.push(location.sublocations);
        sublocations = location.sublocations;
      } else {
        copy.splice(index + 1);
      }
    };

    if (locationValue.length > 0) {
      locationValue.forEach((lv, index) => {
        findSublocation(lv, index);
      });
      setSublocations(copy);
    }
  }, [locationValue]);

  //new location implementation
  const handleChangeLocation = (location, selectedLocationPrev) => {
    setSelectedLocation(location);
    setSelectedLocationPrev(selectedLocationPrev);
    handleOnchange({
      target: {
        name: "asset_id",
        value: "",
      },
    });
    
  };

  useEffect(() => {
    if (selectedLocation) {
      getAsset(selectedLocation.id);
    } else {
      setAssets([]);
    }
  }, [selectedLocation]);

  const handleLocationChange = (e, index) => {
    const location_values = [...locationValue];
    location_values.splice(index);
    location_values.push(e.target.value);
    console.log(location_values);
    setLocationValue(location_values);
  };

  const getAsset = async (location_id) => {
    const res = await asset.list({ location_id });
    if (res.ok) {
      const body = await res.json();
      let data = body.data;
      if (data) {
        data.map((d) => {
          d["component"] = d.asset_components;
          d["name"] = d.asset_name;
          d["value"] = d.id;
        });
        setAssets(data);
      }
    }
  };



  const handleOnchange = (e) => {
    let key = e.target.name;

    setDataToSend((prevData) => ({
      ...prevData,
      [key]: e.target.value,
    }));
  };





  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      let payload = { ...dataToSend };
      payload["location_id"] = selectedLocation?.id;

      const mandatory = [
        "meter_name",
        "unit_id",
        "asset_id",
        "location_id",
      ];
      const ef = getEmptyField(payload, mandatory);
      setEmptyField(ef);
      if (ef.length > 0) {
        toast.error("Mandatory field must be filled");
        return;
      }

      let res;
      if (!editMode) {
        res = await meter.create(payload, attachments);
      } else {
        res = await meter.update(id, payload, attachments);
      }

      setIsLoading(false);
      if (res.ok) {
        toast.success("Successfully Saved");

        if (!editMode) {
          if (afterSubmitCallback) {
            afterSubmitCallback();
          } else {
            backToList();
          }
        }
      } else {
        toast.error("Something went wrong, please try again");
      }
    } catch (e) {
      toast.error(e.message);
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNewUnit = async() => {

    if (!newUnit) return;
    const result = await  meterMeasurementUnit.create({
      name:newUnit
    });

    if (result.ok) {
      const body = await result.json()
      const data = body.data
      setUnits((prev)=>[{...data},...prev])
      setNewUnit("")
    }


  }

  return (
    <div className="container-fluid">
      <LoadingIndicator open={isLoading} />


      <div className="row">
        <div className="col-md-8">
          <Card>
            <CardBody>

                {/* Meter Name */}
                <div className="mb-4">
                  <NormalInput
                    required={true}
                    value={dataToSend["meter_name"]}
                    label="Meter Name"
                    onchange={handleOnchange}
                    name="meter_name"
                    emptyField={emptyField}
                    placeholder="Enter Meter Name (Required)"
                  />
                </div>

                {/* Meter Type */}
                {/* <div className="mb-4">
                  <label className="form-label fw-bold">Meter Type</label>
                  <div className="row">
                    <div className="col-md-6">
                      <div
                        className={`border rounded p-3 text-center cursor-pointer ${
                          dataToSend.meter_type === "manual"
                            ? "border-primary bg-primary bg-opacity-10"
                            : "border-light"
                        }`}
                        onClick={() => setDataToSend({ ...dataToSend, meter_type: "manual" })}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="bi bi-person-circle fs-2 mb-2"></i>
                        <div className="fw-bold">Manual</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div
                        className={`border rounded p-3 text-center cursor-pointer ${
                          dataToSend.meter_type === "automated"
                            ? "border-primary bg-primary bg-opacity-10"
                            : "border-light"
                        }`}
                        onClick={() => setDataToSend({ ...dataToSend, meter_type: "automated" })}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="bi bi-lock fs-2 mb-2"></i>
                        <div className="fw-bold">Automated</div>
                      </div>
                    </div>
                  </div>
                </div> */}

                {/* Description */}
                <div className="mb-4">
                  <NormalTextArea
                    value={dataToSend["description"]}
                    label="Description"
                    onchange={handleOnchange}
                    name="description"
                    rows={3}
                    placeholder="Add a description"
                  />
                </div>

                {/* Measurement Unit */}


              <div className="mb-4">
              <NormalSelect
                required={true}
                value={dataToSend["unit_id"]}
                label="Measurement Unit"
                onchange={handleOnchange}
                list={units}
                name="unit_id"
                emptyField={emptyField}
              valueKey={"id"}
              onSubmit={handleAddNewUnit}
              addMoreComponent={
                <NormalInput
                required={true}
                value={newUnit}
                label="Measurement Unit"
                onchange={(e)=>{setNewUnit(e.target.value)}}
              />
              }
                />
                 </div>

              {/* Asset & Location */}



              <LocationFilter
          type="input"
          onChange={handleChangeLocation}
              defaultValue={selectedLocation}
              defaultOptionsValue={selectedLocationPrev}
              emptyField={emptyField}
        />



                <div className="row mb-4">
                <div className="col-md-12 force-transparent" style={{ zIndex: 9999999 }}>
            <label className="form-label">Asset</label>
            <Select
              styles={{
                ...colourStyles,
                menu: (styles) => ({
                  ...styles,
                  zIndex: 9999,
                  position: "absolute",
                  color: "grey",
                }),
                menuPortal: (styles) => ({
                  ...styles,
                  zIndex: 9999,
                }),
                }}
                  menuPortalTarget={document.body}
                          menuPosition="fixed"
            options={assets.map((i) => {
              return {
                value: i.id,
                label: i.name,
              };
            })}
                value={{
                  value: dataToSend["asset_id"],
                  label: assets.find((i) => i.id == dataToSend["asset_id"])?.name,
            }}
            onChange={(e) => {
              handleOnchange({
                target: {
                  name: "asset_id",
                  value: e.value,
                },
              });
            }}
          />
            </div>

                </div>

                {/* Reading Frequency */}
                <Card>
                  <CardHeader>Reading Frequency</CardHeader>
                  <CardBody>
                <div className="mb-1">

                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <span className="fw-bold">Every:</span>
                    </div>
                    <div className="col-md-3 me-3">
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        value={dataToSend["reading_frequency_value"]}
                        onChange={handleOnchange}
                            name="reading_frequency_value"
                            style={{ backgroundColor: "#0000007a" }}
                          />
                          </div>
                    </div>
                      <div className="col-md-3">

                        <div className="input-group">


                          <select style={{ backgroundColor: "#0000007a" }} className="form-select" value={dataToSend["reading_frequency_unit"]} onChange={handleOnchange} name="reading_frequency_unit">
                            <option value="none">None</option>
                            <option value="minutes">Minutes</option>
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                            <option value="weeks">Weeks</option>
                            <option value="months">Months</option>
                            <option value="years">Years</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  </CardBody>
                </Card>

                {/* Threshold Settings */}
                <Card style={{ marginTop: "10px" }}>
                  <CardHeader>Threshold Settings</CardHeader>
                  <CardBody>
                <div className="mb-4">
                  <div className="mb-3">

                    <p className="text-muted mb-0">Set thresholds according to desired values</p>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                          <span className="fw-bold">Warning:</span>
                        </div>
                        <div className="flex-grow-1">
                          <div className="input-group">
                            <span className="input-group-text">≥</span>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="Set value..."
                              value={dataToSend.warning_threshold}
                              onChange={handleOnchange}
                                  name="warning_threshold"
                                  style={{ backgroundColor: "#0000007a" }}

                            />
                            <span className="input-group-text">{units.find(unit => unit.id === dataToSend.unit_id)?.name || ""}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                          <span className="fw-bold">Alarm:</span>
                        </div>
                        <div className="flex-grow-1">
                          <div className="input-group">
                            <span className="input-group-text">≥</span>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="Set value..."
                              value={dataToSend.alarm_threshold}
                              onChange={handleOnchange}
                                  name="alarm_threshold"
                                  style={{ backgroundColor: "#0000007a" }}
                            />
                            <span className="input-group-text">{units.find(unit => unit.id === dataToSend.unit_id)?.name || ""}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </CardBody>
                </Card>

                <Card style={{ marginTop: "10px" }}>
          <CardHeader>Attachments</CardHeader>
          <CardBody>
            <MultipleFilesAttachments
              attachments={attachments}
              setAttachments={setAttachments}
            ></MultipleFilesAttachments>
          </CardBody>
        </Card>

                {/* Action Buttons */}
                <div className="mt-4 flex">
          <button
            className="btn btn-outline-theme btn-lg me-2"
            onClick={handleSubmit}
          >
            {editMode ? "SAVE" : "ADD"}
          </button>
          <button
            className="btn btn-outline-danger btn-lg"
            onClick={ backToList}
          >
            CANCEL
          </button>
        </div>

            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default AddMeter;
